import React from 'react';
import { X } from 'lucide-react';

const SharedPlanPreviewModal = ({ planData, darkMode, theme, onClose, onMakeCopy, onUseAsTemplate }) => {
  if (!planData) return null;

  const { plan, runs } = planData;

  // Calculate stats
  const totalRuns = Object.keys(runs || {}).length;
  const totalMiles = Object.values(runs || {}).reduce((sum, run) => sum + (run.distance || 0), 0);

  const runTypes = {
    easy: 0,
    recovery: 0,
    long: 0,
    workout: 0,
    race: 0,
    cross: 0,
    rest: 0
  };

  Object.values(runs || {}).forEach(run => {
    if (run.type && runTypes.hasOwnProperty(run.type)) {
      runTypes[run.type]++;
    }
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div
        className={darkMode
          ? 'rounded-2xl p-6 w-full max-w-2xl shadow-2xl bg-slate-800 text-white max-h-[90vh] overflow-y-auto'
          : 'rounded-2xl p-6 w-full max-w-2xl shadow-2xl bg-white max-h-[90vh] overflow-y-auto'
        }
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-2xl">📋</span>
              <h2 className="text-2xl font-bold">Previewing Shared Plan</h2>
            </div>
            <p className={darkMode ? 'text-slate-400 text-sm' : 'text-slate-600 text-sm'}>
              Review this plan before adding it to your library
            </p>
          </div>
          <button
            onClick={onClose}
            className={darkMode ? 'p-2 hover:bg-slate-700 rounded-xl' : 'p-2 hover:bg-slate-100 rounded-xl'}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Plan Info */}
        <div className={darkMode ? 'p-5 rounded-xl border bg-slate-900 border-slate-700 mb-6' : 'p-5 rounded-xl border bg-slate-50 border-slate-200 mb-6'}>
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-xl font-bold mb-1">{plan?.name || 'Training Plan'}</h3>
              <p className={darkMode ? 'text-sm text-slate-400' : 'text-sm text-slate-600'}>
                {plan?.desc || 'Custom training plan'}
              </p>
            </div>
            {plan?.diff && (
              <span className={'px-3 py-1 rounded-full text-xs font-medium ' + theme.light + ' ' + theme.lightText}>
                {plan.diff}
              </span>
            )}
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-3 gap-4 mt-4">
            <div>
              <div className={darkMode ? 'text-2xl font-bold text-white' : 'text-2xl font-bold text-slate-900'}>
                {plan?.weeks?.length || 0}
              </div>
              <div className={darkMode ? 'text-xs text-slate-400' : 'text-xs text-slate-600'}>
                Weeks
              </div>
            </div>
            <div>
              <div className={darkMode ? 'text-2xl font-bold text-white' : 'text-2xl font-bold text-slate-900'}>
                {totalRuns}
              </div>
              <div className={darkMode ? 'text-xs text-slate-400' : 'text-xs text-slate-600'}>
                Runs Scheduled
              </div>
            </div>
            <div>
              <div className={darkMode ? 'text-2xl font-bold text-white' : 'text-2xl font-bold text-slate-900'}>
                {totalMiles.toFixed(1)}
              </div>
              <div className={darkMode ? 'text-xs text-slate-400' : 'text-xs text-slate-600'}>
                Total Miles
              </div>
            </div>
          </div>
        </div>

        {/* Run Breakdown */}
        <div className={darkMode ? 'p-5 rounded-xl border bg-slate-900 border-slate-700 mb-6' : 'p-5 rounded-xl border bg-slate-50 border-slate-200 mb-6'}>
          <h4 className="font-semibold mb-3">Run Breakdown</h4>
          <div className="grid grid-cols-2 gap-3">
            {Object.entries(runTypes).map(([type, count]) => {
              if (count === 0) return null;
              const emoji = {
                easy: '🌱',
                recovery: '❤️‍🩹',
                long: '⏳',
                workout: '💪',
                race: '🏁',
                cross: '🚴',
                rest: '💤'
              }[type];

              return (
                <div key={type} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{emoji}</span>
                    <span className="capitalize text-sm">{type}</span>
                  </div>
                  <span className={darkMode ? 'font-semibold text-slate-300' : 'font-semibold text-slate-700'}>
                    {count}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-3">
          <button
            onClick={onMakeCopy}
            className={'w-full text-white py-3 rounded-xl font-medium ' + theme.primary + ' ' + theme.hover}
          >
            📥 Make a Copy
          </button>
          <button
            onClick={onUseAsTemplate}
            className={darkMode
              ? 'w-full py-3 rounded-xl font-medium bg-slate-700 hover:bg-slate-600'
              : 'w-full py-3 rounded-xl font-medium bg-slate-100 hover:bg-slate-200'
            }
          >
            📋 Use as Template (start fresh)
          </button>
          <button
            onClick={onClose}
            className={darkMode
              ? 'w-full py-3 rounded-xl font-medium text-slate-400 hover:bg-slate-700'
              : 'w-full py-3 rounded-xl font-medium text-slate-600 hover:bg-slate-100'
            }
          >
            Cancel
          </button>
        </div>

        <p className={darkMode ? 'text-xs text-slate-500 text-center mt-4' : 'text-xs text-slate-400 text-center mt-4'}>
          Making a copy will save this plan to your library without replacing your current plan
        </p>
      </div>
    </div>
  );
};

export default SharedPlanPreviewModal;
